import React, { CSSProperties, useEffect, useRef } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import SwiperClass from 'swiper/types/swiper-class';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useLocalStore } from '../../../../../stores';

import styles from './ScheduleCalendar.module.scss';

type Props = {
    style?: CSSProperties,

    title?: {
        schedule: string,
        specific: string,
    },

    calendar: {
        type: '' | 'schedule' | 'specific',
        reset?: boolean,

        schedule: { date: string, time: string[] }[],
        specific: { date: string, time: string[] }[],
    },

    onChange: (calendar: any) => void,
};

type LocalStore = {
    type: '' | 'schedule' | 'specific',

    schedule: { date: string, day: string, range: string[], active: boolean }[];
    specific: { date: string, day: string, range: string[], active: boolean }[];

    selected: {
        ref: { date: string, day: string, range: string[], active: boolean },
        copy: { date: string, day: string, range: string[], active: boolean, changed: boolean },
    } | null,
};

export default observer(function ScheduleCalendar({ style, title, calendar, onChange }: Props) {
    const swiperRef = useRef<SwiperClass>();

    const today = moment();

    const { localStore } = useLocalStore<LocalStore>({
        type: '',

        schedule: [],
        specific: [],

        selected: null,
    });

    useEffect(() => {
        localStore.type = calendar.type;

        if (!localStore.type || calendar.reset) {
            localStore.selected = null;
        }

        localStore.schedule = Array.from({ length: 30 }, (_, i) => today.clone().add(i, 'day'))
            .map((date, i) => ({
                date: date.format('MMM DD'),
                day: date.format('dddd'),
                range: calendar.schedule.find(c => (c.date.length > 6 ? moment(c.date).format('MMM DD') : c.date) === date.format('MMM DD'))?.time || [],
                active: calendar.schedule.some(c => (c.date.length > 6 ? moment(c.date).format('MMM DD') : c.date) === date.format('MMM DD')),
                match: false,
            }));
        localStore.specific = Array.from({ length: 30 }, (_, i) => today.clone().add(i, 'day'))
            .map((date, i) => {
                const range = calendar.specific.find(c => (c.date.length > 6 ? moment(c.date).format('MMM DD') : c.date) === date.format('MMM DD'))?.time || [];
                return {
                    date: date.format('MMM DD'),
                    day: date.format('dddd'),
                    range,
                    active: calendar.specific.some(c => (c.date.length > 6 ? moment(c.date).format('MMM DD') : c.date) === date.format('MMM DD')),
                    match: calendar.schedule.some(c => (c.date.length > 6 ? moment(c.date).format('MMM DD') : c.date) === date.format('MMM DD') && c.time.some(t => range.includes(t))),
                };
            });

        process.nextTick(() => swiperRef.current?.update());
    }, [calendar]);

    return (
        <div className={styles.container} style={style}>
            <div className={styles.tabs}>
                <div
                    className={classNames(
                        styles.tabsItem,
                        {
                            [styles.tabsItemActive]: localStore.type === 'schedule',
                            [styles.tabsItemDisabled]: !!localStore.selected,
                        },
                    )}
                    role="presentation"
                    onClick={() => {
                        localStore.type = localStore.type === 'schedule' ? '' : 'schedule';

                        onChange({
                            type: localStore.type,

                            schedule: localStore.schedule.filter(item => item.range.length).map(item => ({
                                date: item.date,
                                time: item.range,
                            })),
                            specific: localStore.specific.filter(item => item.range.length).map(item => ({
                                date: item.date,
                                time: item.range,
                            })),
                        });
                    }}
                >
                    {title?.schedule || 'As my schedule'}
                </div>
                <div
                    className={classNames(
                        styles.tabsItem,
                        {
                            [styles.tabsItemActive]: localStore.type === 'specific',
                            [styles.tabsItemDisabled]: !!localStore.selected,
                            [styles.tabsItemHidden]: title?.specific === '',
                        },
                    )}
                    role="presentation"
                    onClick={() => {
                        localStore.type = localStore.type === 'specific' ? '' : 'specific';

                        onChange({
                            type: localStore.type,

                            schedule: localStore.schedule.filter(item => item.range.length).map(item => ({
                                date: item.date,
                                time: item.range,
                            })),
                            specific: localStore.specific.filter(item => item.range.length).map(item => ({
                                date: item.date,
                                time: item.range,
                            })),
                        });
                    }}
                >
                    {title?.specific || 'Specific play time'}
                </div>
            </div>

            <Swiper
                className={styles.swiper}
                slidesPerView="auto"
                allowSlideNext={!localStore.type || !localStore.selected}
                allowSlidePrev={!localStore.type || !localStore.selected}
                onSwiper={swiper => (swiperRef.current = swiper)}
            >
                {localStore[localStore.type || 'specific'].map((date: any) => (
                    <SwiperSlide key={date.date} className={styles.swiperSlide}>
                        <div
                            className={classNames(
                                styles.date,
                                {
                                    [styles.dateMatch]: localStore.type && !localStore.selected && date.match,
                                    [styles.dateActive]: localStore.type ? (localStore.selected ? localStore.selected.ref === date : date.active) : false,
                                    [styles.dateDisabled]: !localStore.type || (!!localStore.selected && date !== localStore.selected.ref),
                                },
                            )}
                            role="presentation"
                            onClick={() => {
                                if (!localStore.selected) {
                                    localStore.selected = {
                                        ref: date,
                                        copy: { ...toJS(date), changed: false },
                                    };
                                } else {
                                    localStore.selected = null;
                                }
                            }}
                        >
                            <div>{date.date}</div>
                            <div>
                                {
                                    localStore.type
                                        ? localStore.selected && (localStore.selected.ref === date)
                                            ? (
                                                localStore.selected.copy.range.includes('<12') && localStore.selected.copy.range.includes('12-6') && localStore.selected.copy.range.includes('>6')
                                                    ? 'Any Time'
                                                    : localStore.selected.copy.range.includes('<12') && localStore.selected.copy.range.includes('12-6')
                                                        ? '-Evening'
                                                        : localStore.selected.copy.range.includes('12-6') && localStore.selected.copy.range.includes('>6')
                                                            ? '-Morning'
                                                            : localStore.selected.copy.range.includes('<12') && localStore.selected.copy.range.includes('>6')
                                                                ? '-Afternoon'
                                                                : localStore.selected.copy.range.includes('<12')
                                                                    ? 'Morning'
                                                                    : localStore.selected.copy.range.includes('12-6')
                                                                        ? 'Afternoon'
                                                                        : localStore.selected.copy.range.includes('>6')
                                                                            ? 'Evening'
                                                                            : 'Select Time'
                                            )
                                            : (
                                                date.range.includes('<12') && date.range.includes('12-6') && date.range.includes('>6')
                                                    ? 'Any Time'
                                                    : date.range.includes('<12') && date.range.includes('12-6')
                                                        ? '-Evening'
                                                        : date.range.includes('12-6') && date.range.includes('>6')
                                                            ? '-Morning'
                                                            : date.range.includes('<12') && date.range.includes('>6')
                                                                ? '-Afternoon'
                                                                : date.range.includes('<12')
                                                                    ? 'Morning'
                                                                    : date.range.includes('12-6')
                                                                        ? 'Afternoon'
                                                                        : date.range.includes('>6')
                                                                            ? 'Evening'
                                                                            : date.day
                                            )
                                        : date.day
                                }
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>

            {localStore.type && localStore.selected
                ? (
                    <div className={styles.time}>
                        <div
                            className={styles.button}
                            role="presentation"
                            onClick={() => {
                                if (!localStore.type || !localStore.selected) {
                                    return;
                                }

                                if (!localStore.selected.copy.changed) {
                                    localStore.selected.ref.range = [];
                                    localStore.selected.ref.active = false;
                                }

                                localStore.selected = null;

                                onChange({
                                    type: localStore.type,

                                    schedule: localStore.schedule.filter(item => item.range.length).map(item => ({
                                        date: item.date,
                                        time: item.range,
                                    })),
                                    specific: localStore.specific.filter(item => item.range.length).map(item => ({
                                        date: item.date,
                                        time: item.range,
                                    })),
                                });
                            }}
                        >
                            {localStore.selected.copy.changed ? 'Cancel' : 'No Time'}
                        </div>

                        <div className={styles.path}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="220"
                                height="28"
                                viewBox="0 0 219.402 27.821"
                            >
                                <path
                                    d="M14,5S27.1-2.049,48.981-8.73"
                                    transform="translate(-13.289 21.5)"
                                    fill="none"
                                    stroke="#072c4d"
                                    strokeWidth={localStore.selected.copy.range.includes('<12') ? 3 : 1}
                                />
                                <path
                                    d="M48.981-8.73A255.941,255.941,0,0,1,123-20,256.572,256.572,0,0,1,196.748-8.812"
                                    transform="translate(-13.289 21.5)"
                                    fill="none"
                                    stroke="#072c4d"
                                    strokeWidth={localStore.selected.copy.range.includes('12-6') ? 3 : 1}
                                />
                                <path
                                    d="M48.98,5S35.878-2.049,14-8.73"
                                    transform="translate(169.711 21.5)"
                                    fill="none"
                                    stroke="#072c4d"
                                    strokeWidth={localStore.selected.copy.range.includes('>6') ? 3 : 1}
                                />
                            </svg>

                            <div
                                className={classNames(
                                    styles.ball,
                                    {
                                        [styles.ballActive]: localStore.selected.copy.range.includes('<12'),
                                    },
                                )}
                                style={{ top: -2, left: 20 }}
                                role="presentation"
                                onClick={() => {
                                    if (!localStore.selected) {
                                        return;
                                    }

                                    if (localStore.selected.copy.range.includes('<12')) {
                                        localStore.selected.copy.range = localStore.selected.copy.range.filter(range => range !== '<12');
                                    } else {
                                        localStore.selected.copy.range.push('<12');
                                    }

                                    localStore.selected.copy.changed = true;
                                }}
                            >
                                {'<12'}
                            </div>

                            <div
                                className={classNames(
                                    styles.ball,
                                    {
                                        [styles.ballActive]: localStore.selected.copy.range.includes('12-6'),
                                    },
                                )}
                                style={{ top: -14, left: '50%', transform: 'translateX(-50%)' }}
                                role="presentation"
                                onClick={() => {
                                    if (!localStore.selected) {
                                        return;
                                    }

                                    if (localStore.selected.copy.range.includes('12-6')) {
                                        localStore.selected.copy.range = localStore.selected.copy.range.filter(range => range !== '12-6');
                                    } else {
                                        localStore.selected.copy.range.push('12-6');
                                    }

                                    localStore.selected.copy.changed = true;
                                }}
                            >
                                12-6
                            </div>

                            <div
                                className={classNames(
                                    styles.ball,
                                    {
                                        [styles.ballActive]: localStore.selected.copy.range.includes('>6'),
                                    },
                                )}
                                style={{ top: -2, right: 20 }}
                                role="presentation"
                                onClick={() => {
                                    if (!localStore.selected) {
                                        return;
                                    }

                                    if (localStore.selected.copy.range.includes('>6')) {
                                        localStore.selected.copy.range = localStore.selected.copy.range.filter(range => range !== '>6');
                                    } else {
                                        localStore.selected.copy.range.push('>6');
                                    }

                                    localStore.selected.copy.changed = true;
                                }}
                            >
                                {'6>'}
                            </div>
                        </div>

                        <div
                            className={styles.button}
                            role="presentation"
                            onClick={() => {
                                if (!localStore.type || !localStore.selected) {
                                    return;
                                }

                                if (localStore.selected.copy.changed) {
                                    localStore.selected.ref.range = localStore.selected.copy.range;
                                } else {
                                    localStore.selected.ref.range = ['<12', '12-6', '>6'];
                                }

                                localStore.selected.ref.active = !!localStore.selected.ref.range.length;

                                localStore.selected = null;

                                onChange({
                                    type: localStore.type,

                                    schedule: localStore.schedule.filter(item => item.range.length).map(item => ({
                                        date: item.date,
                                        time: item.range,
                                    })),
                                    specific: localStore.specific.filter(item => item.range.length).map(item => ({
                                        date: item.date,
                                        time: item.range,
                                    })),
                                });
                            }}
                        >
                            {localStore.selected.copy.changed ? 'Ok' : 'Any Time'}
                        </div>
                    </div>
                )
                : (
                    <div className={styles.time}/>
                )}
        </div>
    );
});
